import type {
  GetProjectFilesRequest,
  GetProjectFilesResponse,
  ProjectFileData,
  ProjectFileSource,
  ProjectFileStatus,
} from './getProjectFiles';
import type { UploadProjectFilesRequest } from './uploadProjectFiles';
import type { SaveChatArtefactToProjectRequest } from './saveChatArtefactToProject';
import { MOCK_CONTENT_STRATEGY_CHAT_ID, mockChatId } from './mockProjectChats';
import { addToMockProjectFileCount, findMockProject } from './mockProjects';
import { replaceMockItem } from './mockUtils';

type MockFile = Omit<ProjectFileData, 'fileId' | 'fileType'>;

const KB = 1024;
const MB = 1024 * KB;

const uploaded = (
  fileName: string,
  description: string | null,
  sizeBytes: number,
  status: ProjectFileStatus,
  uploadedDatetime: string,
): MockFile => ({
  fileName,
  description,
  sizeBytes,
  status,
  uploadedDatetime,
  source: 'Uploaded',
  sourceChatThreadId: null,
  sourceChatThreadTitle: null,
});

/** The "Q2 Marketing Strategy" files from the Figma design. */
const DESIGN_FILES: MockFile[] = [
  uploaded(
    'campaign_budget_2026.xlsx',
    'Final approved budget from finance',
    245 * KB,
    'Extracted',
    '2026-05-28T14:30:00Z',
  ),
  uploaded('social_media_plan.docx', 'Social media posting schedule', 128 * KB, 'Extracted', '2026-05-27T10:10:00Z'),
  uploaded('competitor_report_v3.pdf', 'Q1 and Q2 comparison report', 2.1 * MB, 'Processing', '2026-05-28T09:05:00Z'),
  uploaded('brand_guidelines.pdf', 'Corporate identity standards', 5.8 * MB, 'Failed', '2026-05-26T16:20:00Z'),
  {
    fileName: 'email_templates_draft.docx',
    description: 'AI-drafted strategy from research',
    sizeBytes: 89 * KB,
    status: 'Extracted',
    uploadedDatetime: '2026-05-25T11:20:00Z',
    source: 'AI generated',
    sourceChatThreadId: MOCK_CONTENT_STRATEGY_CHAT_ID,
    sourceChatThreadTitle: 'Draft a content strategy based on audience research',
  },
  uploaded('audience_research.pdf', 'Target audience analysis', 1.4 * MB, 'Extracted', '2026-05-24T13:00:00Z'),
  uploaded('Q1_results_summary.xlsx', 'Results from Q1 campaigns', 312 * KB, 'Extracted', '2026-05-22T09:45:00Z'),
  uploaded('content_calendar.xlsx', 'Social media event planner', 156 * KB, 'Extracted', '2026-05-20T15:30:00Z'),
  uploaded('brand_voice_guide.docx', 'Brand tone of voice guidelines', 67 * KB, 'Extracted', '2026-05-18T12:15:00Z'),
  uploaded('channel_performance.pdf', 'Monthly performance metrics', 890 * KB, 'Extracted', '2026-05-15T08:40:00Z'),
  uploaded('budget_forecast_H2.xlsx', 'H2 allocation projections', 198 * KB, 'Extracted', '2026-05-12T17:05:00Z'),
  uploaded(
    'press_release_draft.docx',
    'Press release for product launch',
    45 * KB,
    'Extracted',
    '2026-05-10T10:30:00Z',
  ),
  uploaded('market_analysis_2026.pdf', 'Competitor landscape overview', 3.2 * MB, 'Extracted', '2026-05-08T14:50:00Z'),
  uploaded('team_capacity_plan.xlsx', 'Headcount planning spreadsheet', 112 * KB, 'Extracted', '2026-05-05T11:25:00Z'),
];

const DESIGN_FILES_PROJECT_ID = 'mock-project-1';

const GENERATED_NAMES = [
  'meeting_notes',
  'project_brief',
  'vendor_quote',
  'risk_register',
  'status_update',
  'research_summary',
  'draft_proposal',
  'reference_pack',
] as const;

const GENERATED_EXTENSIONS = ['pdf', 'docx', 'xlsx', 'pptx', 'txt'] as const;

const GENERATED_DESCRIPTIONS = [
  'Notes from the weekly check-in',
  'Initial scope and goals',
  null,
  'Supporting data for review',
  'Latest version shared by the team',
] as const;

/** Mostly extracted, with the occasional file still processing or failed. */
const GENERATED_STATUSES: ProjectFileStatus[] = ['Extracted', 'Extracted', 'Processing', 'Extracted', 'Failed'];

function buildGeneratedFiles(count: number, lastModifiedDatetime: string): MockFile[] {
  const newest = new Date(lastModifiedDatetime);

  return Array.from({ length: count }, (_, i) => {
    const uploadedAt = new Date(newest);
    uploadedAt.setUTCHours(uploadedAt.getUTCHours() - i * 30);
    const isAiGenerated = i % 6 === 4;
    // Every other AI-generated file comes from a chat that has since been deleted.
    const hasSourceChat = isAiGenerated && i % 12 === 4;
    const source: ProjectFileSource = isAiGenerated ? 'AI generated' : 'Uploaded';

    return {
      fileName: `${GENERATED_NAMES[i % GENERATED_NAMES.length]}_${i + 1}.${GENERATED_EXTENSIONS[i % GENERATED_EXTENSIONS.length]}`,
      description: GENERATED_DESCRIPTIONS[i % GENERATED_DESCRIPTIONS.length],
      sizeBytes: ((i * 37) % 900) * KB + 24 * KB,
      status: GENERATED_STATUSES[i % GENERATED_STATUSES.length],
      uploadedDatetime: uploadedAt.toISOString(),
      source,
      sourceChatThreadId: hasSourceChat ? mockChatId(0, i + 1) : null,
      sourceChatThreadTitle: hasSourceChat ? 'Summarise the key points from the uploaded research' : null,
    };
  });
}

const getFileType = (fileName: string) => fileName.split('.').pop()?.toUpperCase() ?? '';

const filesByProjectId = new Map<string, ProjectFileData[]>();

/** Built on first use, so projects created in this session start with no files. */
function getMockProjectFiles(projectId: string): ProjectFileData[] | undefined {
  const cached = filesByProjectId.get(projectId);
  if (cached) return cached;

  const project = findMockProject(projectId);
  if (!project) return undefined;

  const files = (
    projectId === DESIGN_FILES_PROJECT_ID
      ? DESIGN_FILES
      : buildGeneratedFiles(project.numberOfFiles, project.lastModifiedDatetime)
  ).map((file, index) => ({
    ...file,
    fileId: `${projectId}-file-${index + 1}`,
    fileType: getFileType(file.fileName),
  }));

  filesByProjectId.set(projectId, files);
  return files;
}

function compareFiles(a: ProjectFileData, b: ProjectFileData, sortBy: string): number {
  switch (sortBy) {
    case 'fileName':
    case 'description':
    case 'fileType':
    case 'status':
    case 'source':
      return (a[sortBy] ?? '').localeCompare(b[sortBy] ?? '');
    case 'sizeBytes':
      return a.sizeBytes - b.sizeBytes;
    default:
      return new Date(a.uploadedDatetime).getTime() - new Date(b.uploadedDatetime).getTime();
  }
}

/** Applies search, filters, sorting and pagination the way the real endpoint will. Undefined for an unknown project. */
export function queryMockProjectFiles({
  projectId,
  pageIndex,
  pageSize,
  sortBy = 'uploadedDatetime',
  sortOrder = 'desc',
  search,
  statuses,
  sources,
  fileTypes,
}: GetProjectFilesRequest): GetProjectFilesResponse | undefined {
  const allFiles = getMockProjectFiles(projectId);
  if (!allFiles) return undefined;

  const searchTerm = search?.trim().toLowerCase();

  const files = allFiles.filter(
    (file) =>
      (!searchTerm || file.fileName.toLowerCase().includes(searchTerm)) &&
      (!statuses?.length || statuses.includes(file.status)) &&
      (!sources?.length || sources.includes(file.source)) &&
      (!fileTypes?.length || fileTypes.includes(file.fileType)),
  );

  const sorted = [...files].sort((a, b) => {
    const result = compareFiles(a, b, sortBy);
    return sortOrder === 'asc' ? result : -result;
  });

  const start = pageIndex * pageSize;

  return {
    files: sorted.slice(start, start + pageSize),
    numberOfResults: sorted.length,
    fileTypes: [...new Set(allFiles.map((file) => file.fileType))].sort(),
  };
}

/** How long a mock file stays "Processing" before extraction finishes. */
export const MOCK_EXTRACTION_DURATION_MS = 6000;

const byFileId = (fileId: string) => (file: ProjectFileData) => file.fileId === fileId;

function setMockFileStatus(projectId: string, fileId: string, status: ProjectFileStatus) {
  replaceMockItem(filesByProjectId.get(projectId) ?? [], byFileId(fileId), (file) => ({ ...file, status }));
}

/** An empty file has nothing to extract, so it is the one that fails. Retries always succeed. */
function scheduleMockExtraction(projectId: string, fileId: string, shouldFail: boolean) {
  setTimeout(
    () => setMockFileStatus(projectId, fileId, shouldFail ? 'Failed' : 'Extracted'),
    MOCK_EXTRACTION_DURATION_MS,
  );
}

let uploadedFileCount = 0;

/** Adds the files as "Processing", newest first. Undefined for an unknown project. */
export function addMockProjectFiles({ projectId, files }: UploadProjectFilesRequest): ProjectFileData[] | undefined {
  const projectFiles = getMockProjectFiles(projectId);
  if (!projectFiles) return undefined;

  const uploadedDatetime = new Date().toISOString();

  const added = files.map(({ fileName, sizeBytes }) => {
    uploadedFileCount += 1;

    return {
      fileId: `${projectId}-uploaded-file-${uploadedFileCount}`,
      fileName,
      description: null,
      fileType: getFileType(fileName),
      sizeBytes,
      status: 'Processing' as const,
      uploadedDatetime,
      source: 'Uploaded' as const,
      sourceChatThreadId: null,
      sourceChatThreadTitle: null,
    };
  });

  projectFiles.unshift(...added);
  addToMockProjectFileCount(projectId, added.length);
  added.forEach((file) => scheduleMockExtraction(projectId, file.fileId, file.sizeBytes === 0));

  return added;
}

/** Returns false when the file does not exist. */
export function retryMockProjectFile(projectId: string, fileId: string): boolean {
  if (!filesByProjectId.get(projectId)?.some(byFileId(fileId))) return false;

  setMockFileStatus(projectId, fileId, 'Processing');
  scheduleMockExtraction(projectId, fileId, false);
  return true;
}

/** All or nothing: returns false, deleting none, when any of the files does not exist. */
export function deleteMockProjectFiles(projectId: string, fileIds: string[]): boolean {
  const projectFiles = getMockProjectFiles(projectId);
  if (!projectFiles || !fileIds.every((fileId) => projectFiles.some(byFileId(fileId)))) return false;

  const remaining = projectFiles.filter((file) => !fileIds.includes(file.fileId));
  projectFiles.splice(0, projectFiles.length, ...remaining);
  addToMockProjectFileCount(projectId, -fileIds.length);
  return true;
}

/** Returns false when the file does not exist. */
export function updateMockProjectFile(
  projectId: string,
  fileId: string,
  changes: Pick<ProjectFileData, 'description'>,
): boolean {
  return replaceMockItem(filesByProjectId.get(projectId) ?? [], byFileId(fileId), (file) => ({ ...file, ...changes }));
}

/** Which chat draft each saved artefact came from, so a draft is saved only once. */
const draftVersionIdByFileId = new Map<string, string>();

/** No file size comes with a draft, so saved artefacts get a nominal one. */
const SAVED_ARTEFACT_SIZE_BYTES = 32 * KB;

let savedArtefactCount = 0;

/** Adds a chat's draft as an AI-generated file, or replaces the file it was already saved as. Undefined for an unknown project. */
export function saveMockChatArtefact({
  projectId,
  chatId,
  chatTitle,
  draftVersionId,
  fileName,
}: SaveChatArtefactToProjectRequest): ProjectFileData | undefined {
  const projectFiles = getMockProjectFiles(projectId);
  if (!projectFiles) return undefined;

  const existing = projectFiles.find((file) => draftVersionIdByFileId.get(file.fileId) === draftVersionId);
  if (existing) {
    const updated: ProjectFileData = { ...existing, status: 'Processing', uploadedDatetime: new Date().toISOString() };
    replaceMockItem(projectFiles, byFileId(existing.fileId), () => updated);
    scheduleMockExtraction(projectId, existing.fileId, false);
    return updated;
  }

  savedArtefactCount += 1;
  const file: ProjectFileData = {
    fileId: `${projectId}-artefact-${savedArtefactCount}`,
    fileName,
    description: null,
    fileType: getFileType(fileName),
    sizeBytes: SAVED_ARTEFACT_SIZE_BYTES,
    status: 'Processing',
    uploadedDatetime: new Date().toISOString(),
    source: 'AI generated',
    sourceChatThreadId: chatId,
    sourceChatThreadTitle: chatTitle,
  };

  projectFiles.unshift(file);
  draftVersionIdByFileId.set(file.fileId, draftVersionId);
  addToMockProjectFileCount(projectId, 1);
  scheduleMockExtraction(projectId, file.fileId, false);

  return file;
}

const failedArtefactDraftIds = new Set<string>();

/** A draft whose filename contains "fail" fails its first save, so the error can be tried out; saving it again succeeds. */
export function shouldMockArtefactSaveFail({
  draftVersionId,
  fileName,
}: Pick<SaveChatArtefactToProjectRequest, 'draftVersionId' | 'fileName'>): boolean {
  if (!/fail/i.test(fileName) || failedArtefactDraftIds.has(draftVersionId)) return false;

  failedArtefactDraftIds.add(draftVersionId);
  return true;
}

/** Drafts saved to the project whose files haven't been deleted. Undefined for an unknown project. */
export function getMockSavedDraftVersionIds(projectId: string): string[] | undefined {
  const projectFiles = getMockProjectFiles(projectId);
  if (!projectFiles) return undefined;

  return projectFiles.flatMap((file) => {
    const draftVersionId = draftVersionIdByFileId.get(file.fileId);
    return draftVersionId ? [draftVersionId] : [];
  });
}
